import {HoistModel, LoadSpec, managed, TaskObserver, XH} from '@xh/hoist/core';
import {action, observableRef, runInAction} from '@xh/hoist/mobx';
import {ChartModel} from '@xh/hoist/cmp/chart';
import Highcharts from 'highcharts/highstock';
import {BENCHMARK_COLOR, CHART_DOWN_COLOR, CHART_UP_COLOR} from '../../../../cmp/ChartColors';
import {PerformanceSeriesMeta} from '../historicPerformance/HistoricPerformanceWidgetModel';

export class YoyWidgetModel extends HoistModel {
    apiTask = TaskObserver.trackLast();
    @managed chartModel = new ChartModel({highchartsConfig: this.createChartModel()});
    @observableRef accessor seriesMeta: PerformanceSeriesMeta[] = [];

    override async doLoadAsync(loadSpec: LoadSpec): Promise<void> {
        const data = await XH.performanceService.fetchYoOYReturnsAsync(),
            isVisible = (key: string) => this.seriesMeta.find(m => m.key === key)?.visible ?? true;

        const seriesDefs: PerformanceSeriesMeta[] = [
            {key: 'snp', name: 'S&P 500', color: BENCHMARK_COLOR, visible: isVisible('snp')},
            {key: 'return', name: 'Return', color: CHART_UP_COLOR, visible: isVisible('return')},
            {key: 'dva', name: 'DVA Return', color: `${CHART_UP_COLOR}80`, visible: isVisible('dva')}
        ];

        let snpSeries = {
            id: 'snp',
            name: seriesDefs[0].name,
            color: seriesDefs[0].color,
            visible: seriesDefs[0].visible,
            data: data.map(d => d.snpReturnPct * 100)
        };

        // The portfolio's own returns are colored by sign, the benchmark keeps one color.
        let returnSeries = {
            id: 'return',
            name: seriesDefs[1].name,
            color: seriesDefs[1].color,
            negativeColor: CHART_DOWN_COLOR,
            visible: seriesDefs[1].visible,
            data: data.map(d => d.returnPct * 100)
        };

        let dvaReturnSeries = {
            id: 'dva',
            name: seriesDefs[2].name,
            color: seriesDefs[2].color,
            negativeColor: `${CHART_DOWN_COLOR}80`,
            visible: seriesDefs[2].visible,
            data: data.map(d => d.dvaReturnPct * 100)
        };

        runInAction(() => {
            this.chartModel.updateHighchartsConfig({
                xAxis: {categories: data.map(d => d.year.toString())}
            });
            this.chartModel.setSeries([snpSeries, returnSeries, dvaReturnSeries]);
            this.seriesMeta = seriesDefs;
        });
    }

    /** Toggles a series' visibility - clicked via its color-matched toolbar button. */
    @action
    toggleSeries(key: string) {
        const meta = this.seriesMeta.find(m => m.key === key);
        if (!meta) return;

        const visible = !meta.visible;
        this.seriesMeta = this.seriesMeta.map(m => (m.key === key ? {...m, visible} : m));
        (this.chartModel.highchart?.get(key) as Highcharts.Series)?.setVisible(visible, true);
    }

    private createChartModel() {
        return {
            chart: {type: 'column', animation: false},
            // Series are toggled from the toolbar instead.
            legend: {enabled: false},
            xAxis: {},
            yAxis: {
                title: {text: null},
                labels: {format: '{value}%'},
                gridLineDashStyle: 'Dot',
                // Fit the axis to the bars - snapping its ends to the next tick left up to a whole tick of empty space
                startOnTick: false,
                endOnTick: false,
                minPadding: 0.05,
                maxPadding: 0.05,
                plotLines: [{value: 0, width: 1, color: 'rgba(128, 128, 128, 0.6)', zIndex: 3}]
            },
            plotOptions: {
                series: {animation: false},
                column: {
                    borderWidth: 0,
                    borderRadius: 1,
                    groupPadding: 0.12,
                    dataLabels: {enabled: false, format: '{point.y:.2f}%'}
                }
            },
            tooltip: {
                shared: true,
                headerFormat: '<b>{point.key}</b><br/>',
                pointFormat: '<span style="color:{series.color}">●</span> {series.name}: <b>{point.y:.2f}%</b><br/>'
            }
        };
    }
}
